import React from "react";
import { X, ExternalLink } from "lucide-react";
import { IProduct, ICompatibilityResponse } from "../types";
import { formatPrice } from "../utils/price";
import { apiService } from "../services/api";

interface PricingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedComponents: Record<string, IProduct>;
  compatibilityData?: ICompatibilityResponse | null;
}

export const PricingDetailsModal: React.FC<PricingDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedComponents,
  compatibilityData,
}) => {
  if (!isOpen) return null;

  // Get the rel attribute string from global settings
  const affiliateLinkRel = apiService.getAffiliateLinkRel();

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getBrandName = (brand: any): string => {
    if (typeof brand === "string") return brand;
    if (typeof brand === "object" && brand !== null) {
      return brand.name || "Unknown Brand";
    }
    return "Unknown Brand";
  };

  const getProviderName = (provider: any): string => {
    if (typeof provider === "string") return provider;
    if (typeof provider === "object" && provider !== null) {
      return provider.name || "Direct";
    }
    return "Direct";
  };

  const handleLinkClick = (
    product: IProduct,
    provider: string,
    region: string,
    price?: any,
  ) => {
    // Track the link click
    // Check for override/provider rule at price level first, then product level
    const isOverridden =
      price?.is_overridden || !!(product as any).is_overridden || false;
    const hasProviderRule =
      price?.has_provider_rule || !!(product as any).has_provider_rule || false;

    apiService.trackLinkClick(
      product.id.toString(),
      product.name,
      getBrandName(product.brand || (product as any).manufacturer),
      provider,
      region,
      isOverridden,
      hasProviderRule,
    );
  };

  return (
    <div
      className="srb-fixed srb-inset-0 srb-z-modal srb-flex srb-items-center srb-justify-center srb-p-4"
      style={{ backgroundColor: "var(--srb-overlay-bg)" }}
      onClick={handleBackdropClick}
    >
      <div
        className="srb-bg-background srb-border srb-border-subtle srb-w-full srb-max-w-4xl srb-max-h-[80vh] srb-overflow-hidden srb-flex srb-flex-col"
        style={(function () {
          const root = document.querySelector(".sim-rig-builder");
          if (!root) return {};
          const borderColor = getComputedStyle(root)
            .getPropertyValue("--srb-secondary-color")
            .trim();
          return { boxShadow: `0 0 0 1px ${borderColor}` };
        })()}
      >
        <div className="srb-flex srb-justify-between srb-items-center srb-p-4 md:srb-p-6 srb-border-b srb-border-subtle">
          <h2 className="srb-text-xl md:srb-text-2xl srb-font-bold srb-text-main">
            Build Pricing Details
          </h2>
          <button
            onClick={onClose}
            className="srb-p-2 srb-border-none !srb-bg-transparent srb-text-main hover:srb-text-primary srb-cursor-pointer srb-transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="srb-flex-1 srb-overflow-y-auto srb-p-4 md:srb-p-6">
          <div className="srb-space-y-4 md:srb-space-y-6">
            {Object.entries(selectedComponents).map(([categoryId, product]) => {
              // Handle images array safely
              let primaryImage = null;
              if (
                product.images &&
                Array.isArray(product.images) &&
                product.images.length > 0
              ) {
                primaryImage =
                  product.images.find((img) => img.is_primary) ||
                  product.images[0];
              }
              const imageUrl =
                primaryImage?.image || (product as any).image || null;

              return (
                <div
                  key={categoryId}
                  className="srb-border srb-border-subtle srb-bg-hover srb-p-3 md:srb-p-4"
                >
                  <div className="srb-flex srb-gap-3 md:srb-gap-4 srb-mb-3 md:srb-mb-4">
                    {imageUrl && (
                      <div className="srb-relative srb-w-16 srb-h-16 md:srb-w-20 md:srb-h-20 srb-flex-shrink-0 srb-bg-white">
                        <img
                          src={imageUrl}
                          alt={product.name}
                          className="srb-w-full srb-h-full srb-object-contain"
                        />
                      </div>
                    )}
                    <div className="srb-flex-1 srb-flex srb-flex-col srb-justify-start srb-gap-1 srb-items-start srb-min-w-0">
                      <div className="srb-text-xs srb-text-muted srb-uppercase srb-tracking-wider srb-m-0">
                        {categoryId}
                      </div>
                      <h3 className="srb-text-base md:srb-text-lg srb-font-semibold srb-text-main srb-m-0 srb-break-words">
                        {product.name}
                      </h3>
                      <p className="srb-text-sm srb-text-muted srb-m-0">
                        {getBrandName(
                          product.brand || (product as any).manufacturer,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="srb-space-y-2">
                    {product.prices && product.prices.length > 0 ? (
                      product.prices.map((price, index) => (
                        <div
                          key={index}
                          className="srb-bg-background srb-border srb-border-subtle srb-p-3 srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-3 md:srb-justify-between"
                        >
                          <div className="srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-2 md:srb-gap-3 srb-flex-1 srb-w-full md:srb-w-auto">
                            <div className="srb-flex srb-items-center srb-gap-2 srb-flex-wrap">
                              <span className="srb-text-sm srb-text-main">
                                {getProviderName(price.provider)}
                              </span>
                              <span className="srb-font-bold srb-text-main">
                                ({price.region})
                              </span>
                            </div>

                            <div className="srb-flex srb-items-center srb-gap-2 srb-flex-wrap">
                              <span className="srb-text-main srb-font-bold">
                                {formatPrice(price.price, 2, price.currency)}
                              </span>
                              <span
                                className={`srb-text-xs srb-px-2 srb-py-1 srb-rounded ${
                                  price.availability === "in_stock"
                                    ? "srb-bg-green-500/20 srb-text-green-400"
                                    : price.availability === "coming_soon"
                                      ? "srb-bg-yellow-500/20 srb-text-yellow-400"
                                      : "srb-bg-red-500/20 srb-text-red-400"
                                }`}
                              >
                                {price.availability === "in_stock"
                                  ? "In Stock"
                                  : price.availability === "coming_soon"
                                    ? "Coming Soon"
                                    : "Out of Stock"}
                              </span>
                            </div>

                            {/* Discount Info for prices array */}
                            {(price.discount_code || price.discount_amount) && (
                              <div className="srb-flex srb-flex-wrap srb-gap-2">
                                {price.discount_amount && (
                                  <span className="srb-inline-block srb-text-green-400 srb-text-xs srb-font-semibold">
                                    {price.discount_amount}
                                  </span>
                                )}
                                {price.discount_code && (
                                  <span className="srb-inline-block srb-text-primary srb-text-xs srb-font-mono">
                                    Code: {price.discount_code}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          <a
                            href={
                              price.link_to_provider || product.link_to_provider
                            }
                            target="_blank"
                            rel={affiliateLinkRel}
                            onClick={() =>
                              handleLinkClick(
                                product,
                                getProviderName(price.provider),
                                price.region,
                                price,
                              )
                            }
                            className="srb-bg-primary srb-no-underline hover:srb-opacity-90 srb-text-white srb-px-4 srb-py-2 srb-font-semibold srb-flex srb-items-center srb-gap-2 srb-transition srb-whitespace-nowrap srb-w-full md:srb-w-auto srb-justify-center"
                          >
                            Buy Now
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      ))
                    ) : (
                      <div className="srb-bg-background srb-border srb-border-subtle srb-p-3 srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-3 md:srb-justify-between">
                        <div className="srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-2 md:srb-gap-3 srb-flex-1 srb-w-full md:srb-w-auto">
                          <span className="srb-text-sm srb-text-main">
                            Direct
                          </span>

                          <div className="srb-flex srb-items-center srb-gap-2">
                            <span className="srb-text-main srb-font-bold">
                              {formatPrice(product.price, 2)}
                            </span>
                          </div>

                          {/* Discount Info for direct products */}
                          {(product.discount_code ||
                            product.discount_amount) && (
                            <div className="srb-flex srb-flex-wrap srb-gap-2">
                              {product.discount_amount && (
                                <span className="srb-inline-block srb-text-green-400 srb-text-xs srb-font-semibold">
                                  {product.discount_amount}
                                </span>
                              )}
                              {product.discount_code && (
                                <span className="srb-inline-block srb-text-primary srb-text-xs srb-font-mono">
                                  Code: {product.discount_code}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <a
                          href={product.link_to_provider}
                          target="_blank"
                          rel={affiliateLinkRel}
                          onClick={() =>
                            handleLinkClick(product, "Direct", "US", undefined)
                          }
                          className="srb-bg-primary srb-no-underline hover:srb-opacity-90 srb-text-white srb-px-4 srb-py-2 srb-font-semibold srb-flex srb-items-center srb-gap-2 srb-transition srb-whitespace-nowrap srb-w-full md:srb-w-auto srb-justify-center"
                        >
                          Buy Now
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Display adapters if they exist */}
            {compatibilityData &&
              (compatibilityData.wheelbaseAdapter ||
                compatibilityData.wheelAdapter) && (
                <div className="srb-border-t srb-border-yellow-600/50 srb-pt-6 srb-mt-2">
                  <div className="srb-mb-4">
                    <h3 className="srb-text-xl srb-font-bold srb-text-yellow-400 srb-mb-2">
                      Required Adapters
                    </h3>
                    <p className="srb-text-sm srb-text-muted">
                      {compatibilityData.message ||
                        "These adapters are required for compatibility"}
                    </p>
                  </div>

                  <div className="srb-space-y-4">
                    {compatibilityData.wheelbaseAdapter && (
                      <div className="srb-border srb-border-subtle srb-bg-hover srb-p-3 md:srb-p-4">
                        <div className="srb-flex srb-gap-3 md:srb-gap-4 srb-mb-3 md:srb-mb-4">
                          {compatibilityData.wheelbaseAdapter.images[0] && (
                            <div className="srb-relative srb-w-16 srb-h-16 md:srb-w-20 md:srb-h-20 srb-flex-shrink-0 srb-bg-white">
                              <img
                                src={
                                  compatibilityData.wheelbaseAdapter.images[0]
                                    .image
                                }
                                alt={compatibilityData.wheelbaseAdapter.name}
                                className="srb-w-full srb-h-full srb-object-contain"
                              />
                            </div>
                          )}
                          <div className="srb-flex-1 srb-flex srb-flex-col srb-justify-start srb-gap-1 srb-items-start srb-min-w-0">
                            <div className="srb-text-xs srb-text-yellow-400 srb-uppercase srb-tracking-wider srb-m-0 srb-font-semibold">
                              WHEELBASE ADAPTER
                            </div>
                            <h3 className="srb-text-base md:srb-text-lg srb-font-semibold srb-text-main srb-m-0 srb-break-words">
                              {compatibilityData.wheelbaseAdapter.name}
                            </h3>
                            <p className="srb-text-sm srb-text-muted srb-m-0">
                              {compatibilityData.wheelbaseAdapter.brand.name}
                            </p>
                          </div>
                        </div>

                        <div className="srb-bg-background srb-border srb-border-subtle srb-p-3 srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-3 md:srb-justify-between">
                          <div className="srb-flex srb-flex-wrap srb-items-center srb-gap-2 srb-flex-1">
                            <span className="srb-text-main srb-font-bold">
                              {formatPrice(
                                compatibilityData.wheelbaseAdapter
                                  .discounted_price ||
                                  compatibilityData.wheelbaseAdapter.price,
                                2,
                              )}
                            </span>
                            {compatibilityData.wheelbaseAdapter
                              .discount_percentage &&
                              compatibilityData.wheelbaseAdapter
                                .discount_percentage > 0 && (
                                <span className="srb-text-xs srb-px-2 srb-py-1 srb-rounded srb-bg-green-500/20 srb-text-green-400">
                                  {
                                    compatibilityData.wheelbaseAdapter
                                      .discount_percentage
                                  }
                                  % OFF
                                </span>
                              )}
                            {compatibilityData.wheelbaseAdapter
                              .discount_code && (
                              <span className="srb-text-xs srb-text-primary srb-font-mono">
                                Code:{" "}
                                {
                                  compatibilityData.wheelbaseAdapter
                                    .discount_code
                                }
                              </span>
                            )}
                          </div>

                          <a
                            href={compatibilityData.wheelbaseAdapter.link}
                            target="_blank"
                            rel={affiliateLinkRel}
                            onClick={() => {
                              if (compatibilityData.wheelbaseAdapter) {
                                apiService.trackLinkClick(
                                  compatibilityData.wheelbaseAdapter.id.toString(),
                                  compatibilityData.wheelbaseAdapter.name,
                                  compatibilityData.wheelbaseAdapter.brand.name,
                                  "Direct",
                                  "US",
                                  false,
                                  false,
                                );
                              }
                            }}
                            className="srb-bg-primary srb-no-underline hover:srb-opacity-90 srb-text-white srb-px-4 srb-py-2 srb-font-semibold srb-flex srb-items-center srb-gap-2 srb-transition srb-whitespace-nowrap srb-w-full md:srb-w-auto srb-justify-center"
                          >
                            Buy Now
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </div>
                    )}

                    {compatibilityData.wheelAdapter && (
                      <div className="srb-border srb-border-subtle srb-bg-hover srb-p-3 md:srb-p-4">
                        <div className="srb-flex srb-gap-3 md:srb-gap-4 srb-mb-3 md:srb-mb-4">
                          {compatibilityData.wheelAdapter.images[0] && (
                            <div className="srb-relative srb-w-16 srb-h-16 md:srb-w-20 md:srb-h-20 srb-flex-shrink-0 srb-bg-white">
                              <img
                                src={
                                  compatibilityData.wheelAdapter.images[0].image
                                }
                                alt={compatibilityData.wheelAdapter.name}
                                className="srb-w-full srb-h-full srb-object-contain"
                              />
                            </div>
                          )}
                          <div className="srb-flex-1 srb-flex srb-flex-col srb-justify-start srb-gap-1 srb-items-start srb-min-w-0">
                            <div className="srb-text-xs srb-text-yellow-400 srb-uppercase srb-tracking-wider srb-m-0 srb-font-semibold">
                              WHEEL ADAPTER
                            </div>
                            <h3 className="srb-text-base md:srb-text-lg srb-font-semibold srb-text-main srb-m-0 srb-break-words">
                              {compatibilityData.wheelAdapter.name}
                            </h3>
                            <p className="srb-text-sm srb-text-muted srb-m-0">
                              {compatibilityData.wheelAdapter.brand.name}
                            </p>
                          </div>
                        </div>

                        <div className="srb-bg-background srb-border srb-border-subtle srb-p-3 srb-flex srb-flex-col md:srb-flex-row srb-items-start md:srb-items-center srb-gap-3 md:srb-justify-between">
                          <div className="srb-flex srb-flex-wrap srb-items-center srb-gap-2 srb-flex-1">
                            <span className="srb-text-main srb-font-bold">
                              {formatPrice(
                                compatibilityData.wheelAdapter
                                  .discounted_price ||
                                  compatibilityData.wheelAdapter.price,
                                2,
                              )}
                            </span>
                            {compatibilityData.wheelAdapter
                              .discount_percentage &&
                              compatibilityData.wheelAdapter
                                .discount_percentage > 0 && (
                                <span className="srb-text-xs srb-px-2 srb-py-1 srb-rounded srb-bg-green-500/20 srb-text-green-400">
                                  {
                                    compatibilityData.wheelAdapter
                                      .discount_percentage
                                  }
                                  % OFF
                                </span>
                              )}
                            {compatibilityData.wheelAdapter.discount_code && (
                              <span className="srb-text-xs srb-text-primary srb-font-mono">
                                Code:{" "}
                                {compatibilityData.wheelAdapter.discount_code}
                              </span>
                            )}
                          </div>

                          <a
                            href={compatibilityData.wheelAdapter.link}
                            target="_blank"
                            rel={affiliateLinkRel}
                            onClick={() => {
                              if (compatibilityData.wheelAdapter) {
                                apiService.trackLinkClick(
                                  compatibilityData.wheelAdapter.id.toString(),
                                  compatibilityData.wheelAdapter.name,
                                  compatibilityData.wheelAdapter.brand.name,
                                  "Direct",
                                  "US",
                                  false,
                                  false,
                                );
                              }
                            }}
                            className="srb-bg-primary srb-no-underline hover:srb-opacity-90 srb-text-white srb-px-4 srb-py-2 srb-font-semibold srb-flex srb-items-center srb-gap-2 srb-transition srb-whitespace-nowrap srb-w-full md:srb-w-auto srb-justify-center"
                          >
                            Buy Now
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>

        <div className="srb-p-4 md:srb-p-6 srb-border-t srb-border-subtle srb-bg-hover">
          <div className="srb-flex srb-flex-col md:srb-flex-row srb-gap-3 md:srb-justify-between md:srb-items-center">
            <p className="srb-text-muted srb-text-sm srb-hidden md:srb-block">
              Compare prices and choose the best option for each component
            </p>
            <button
              onClick={onClose}
              className="srb-px-6 srb-cursor-pointer srb-border-none srb-py-2 srb-bg-hover hover:srb-bg-background srb-border srb-border-subtle srb-text-main srb-font-semibold srb-transition srb-w-full md:srb-w-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
